(() => {
  const dataSelector = '[data-product-performance-data]';
  const hostSelector = '.product-tabs-shell__side-content';
  const temperatureDomain = { min: -25, max: 25 };
  const temperatureBands = [
    { pattern: /unter\s*-?\s*15\s*°\s*c/i, min: -20, max: -15 },
    { pattern: /-10\s*°\s*c\s*bis\s*-15\s*°\s*c/i, min: -15, max: -10 },
    { pattern: /-5\s*°\s*c\s*bis\s*-10\s*°\s*c/i, min: -10, max: -5 },
    { pattern: /0\s*°\s*c\s*bis\s*-5\s*°\s*c/i, min: -5, max: 0 },
    { pattern: /über\s*0\s*°\s*c\s*bis\s*5\s*°\s*c/i, min: 0, max: 5 },
    { pattern: /über\s*5\s*°\s*c\s*bis\s*10\s*°\s*c/i, min: 5, max: 10 },
    { pattern: /über\s*10\s*°\s*c/i, min: 10, max: 20 },
  ];

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
  const formatNumber = new Intl.NumberFormat(document.documentElement.lang || 'de');
  const formatDecimal = new Intl.NumberFormat(document.documentElement.lang || 'de', {
    maximumFractionDigits: 1,
  });
  const formatWeight = (weight) => Number.isFinite(weight) ? `${formatNumber.format(Math.round(weight))} g` : '–';
  const formatTemperature = (temperature) => `${temperature > 0 ? '+' : ''}${temperature} °C`;
  const formatPackDimensions = (dimensions) => (
    `Ø ${formatDecimal.format(dimensions.diameter)} × ${formatDecimal.format(dimensions.height)} cm`
  );
  const formatPackVolume = (volume) => `${formatDecimal.format(volume)} l`;
  const parseWeight = (rawWeight) => {
    const value = rawWeight && typeof rawWeight === 'object'
      ? rawWeight.value ?? rawWeight.amount ?? rawWeight.weight
      : rawWeight;
    const unit = rawWeight && typeof rawWeight === 'object'
      ? rawWeight.unit ?? rawWeight.unitCode ?? ''
      : '';
    const parsed = typeof value === 'number'
      ? value
      : Number.parseFloat(String(value ?? '').replace(',', '.').replace(/[^0-9+-.]/g, ''));

    if (!Number.isFinite(parsed)) return null;

    const normalisedUnit = `${unit} ${value ?? ''}`.toLowerCase();
    if (normalisedUnit.includes('kg') || normalisedUnit.includes('kilogram')) return parsed * 1000;
    if (normalisedUnit.includes('oz') || normalisedUnit.includes('ounce')) return parsed * 28.3495;
    if (normalisedUnit.includes('lb') || normalisedUnit.includes('pound')) return parsed * 453.592;
    return parsed;
  };
  const parseTemperature = (value) => {
    const match = String(value ?? '').replace(',', '.').match(/-?\d+(?:\.\d+)?/);
    const parsed = match ? Number.parseFloat(match[0]) : Number.NaN;
    return Number.isFinite(parsed) ? parsed : null;
  };
  const updateQuickFactTemperatures = (temperature) => {
    const comfort = parseTemperature(temperature?.comfort);
    const limit = parseTemperature(temperature?.limit);

    if (comfort === null || limit === null) return;

    const fact = document.querySelector('.product-quick-facts__item:first-child');
    if (fact?.dataset.temperatureSource === 'sleep-limit') return;
    const label = fact?.querySelector('.product-quick-facts__label');
    const value = fact?.querySelector('.product-quick-facts__value');

    if (!label || !value) return;

    label.textContent = 'Komfort / Limit';
    value.textContent = `${formatTemperature(comfort)} / ${formatTemperature(limit)}`;
    fact.setAttribute(
      'aria-label',
      `Komforttemperatur ${formatTemperature(comfort)}. Limittemperatur ${formatTemperature(limit)}.`,
    );
  };
  const parsePackDimensions = (rawDimensions) => {
    const parseDimension = (value) => {
      const rawValue = value && typeof value === 'object'
        ? value.value ?? value.amount ?? value.dimension
        : value;
      const unit = value && typeof value === 'object'
        ? String(value.unit ?? value.unitCode ?? '').toLowerCase()
        : '';
      const match = String(rawValue ?? '').replace(',', '.').match(/\d+(?:\.\d+)?/);
      const parsed = match ? Number.parseFloat(match[0]) : Number.NaN;

      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      if (unit.includes('mm')) return parsed / 10;
      if (unit === 'm' || unit.includes('meter')) return parsed * 100;
      if (unit.includes('in')) return parsed * 2.54;
      return parsed;
    };
    let values = [];

    if (Array.isArray(rawDimensions)) {
      values = rawDimensions.map(parseDimension).filter((value) => value !== null);
    } else {
      const source = rawDimensions && typeof rawDimensions === 'object'
      ? rawDimensions.value ?? rawDimensions.dimensions ?? rawDimensions.dimension
      : rawDimensions;

      if (Array.isArray(source)) {
        values = source.map(parseDimension).filter((value) => value !== null);
      } else {
        const text = String(source ?? '').trim();

        if (!text) return null;

        if (/[x×]/iu.test(text)) {
          values = text
            .split(/[x×]/iu)
            .map(parseDimension)
            .filter((value) => value !== null);
        } else {
          const commaSeparated = text.match(/^\s*(\d+(?:[.,]\d+)?)\s*[,;/]\s*(\d+(?:[.,]\d+)?)\s*(?:cm)?\s*$/iu);

          if (commaSeparated) {
            values = [parseDimension(commaSeparated[1]), parseDimension(commaSeparated[2])];
          } else {
            values = (text.match(/\d+(?:[.,]\d+)?/gu) || [])
              .map(parseDimension)
              .filter((value) => value !== null);
          }
        }
      }
    }

    const [diameter, height] = values;

    if (!Number.isFinite(diameter) || !Number.isFinite(height)) return null;

    return {
      diameter,
      height,
      volume: (Math.PI * ((diameter / 2) ** 2) * height) / 1000,
    };
  };
  const packIconSizeFor = (dimensions, minimumVolume, maximumVolume) => {
    const hasRange = Number.isFinite(minimumVolume)
      && Number.isFinite(maximumVolume)
      && maximumVolume > minimumVolume;
    const volumeProgress = hasRange
      ? clamp((dimensions.volume - minimumVolume) / (maximumVolume - minimumVolume), 0, 1)
      : 0.5;
    const width = 24 + (10 * volumeProgress);
    const ratio = clamp(dimensions.height / dimensions.diameter, 1, 2);

    return {
      width: clamp(width, 24, 34),
      height: clamp(width * ratio, 32, 64),
    };
  };

  const temperatureRangeFor = (tags) => {
    const matches = (Array.isArray(tags) ? tags : []).flatMap((tag) => (
      temperatureBands.filter((band) => band.pattern.test(String(tag)))
    ));

    if (!matches.length) return null;

    return matches.reduce((range, band) => ({
      min: Math.min(range.min, band.min),
      max: Math.max(range.max, band.max),
    }), { min: matches[0].min, max: matches[0].max });
  };

  const temperatureProfileFor = (temperature) => {
    const points = [
      { key: 'limit', label: 'Limit', value: parseTemperature(temperature?.limit) },
      { key: 'comfort', label: 'Komfort', value: parseTemperature(temperature?.comfort) },
      { key: 'max', label: 'Max', value: parseTemperature(temperature?.max) },
    ].filter((point) => point.value !== null);

    if (!points.length) return null;

    return {
      points,
      min: Math.min(...points.map((point) => point.value)),
      max: Math.max(...points.map((point) => point.value)),
    };
  };

  const positionForTemperature = (temperature) => clamp(
    ((temperature - temperatureDomain.min) / (temperatureDomain.max - temperatureDomain.min)) * 100,
    0,
    100,
  );

  const temperatureColorFor = (position) => {
    const cold = [18, 109, 197];
    const middle = [118, 84, 173];
    const warm = [209, 43, 50];
    const progress = clamp(position, 0, 100) / 100;
    const [start, end, localProgress] = progress <= 0.5
      ? [cold, middle, progress * 2]
      : [middle, warm, (progress - 0.5) * 2];
    const color = start.map((channel, index) => Math.round(channel + ((end[index] - channel) * localProgress)));

    return `rgb(${color.join(', ')})`;
  };

  const mountMeters = () => {
    const dataNode = document.querySelector(dataSelector);
    if (!dataNode) return;

    let data;
    try {
      data = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }

    updateQuickFactTemperatures(data.temperature);

    const host = document.querySelector(hostSelector);

    if (!host || host.dataset.performanceMetersMounted === 'true') return;

    const temperatureProfile = temperatureProfileFor(data.temperature);
    const temperatureRange = temperatureProfile || temperatureRangeFor(data.temperatureTags);
    const temperatureStart = temperatureRange
      ? positionForTemperature(temperatureRange.min)
      : 50;
    const temperatureEnd = temperatureRange
      ? positionForTemperature(temperatureRange.max)
      : 50;
    const temperaturePoints = temperatureProfile
      ? temperatureProfile.points.map((point) => ({
        ...point,
        position: positionForTemperature(point.value),
      }))
      : [];
    const hasProductZero = temperaturePoints.some((point) => point.value === 0);
    const packDimensions = parsePackDimensions(data.packDimensions);
    const packScaleDimensions = (Array.isArray(data.packDimensionScaleValues)
      ? data.packDimensionScaleValues
      : [])
      .map(parsePackDimensions)
      .filter(Boolean);
    const packVolumes = packScaleDimensions.map((dimensions) => dimensions.volume);
    const minimumPackVolume = packVolumes.length ? Math.min(...packVolumes) : null;
    const maximumPackVolume = packVolumes.length ? Math.max(...packVolumes) : null;
    const hasPackScale = packDimensions
      && Number.isFinite(minimumPackVolume)
      && Number.isFinite(maximumPackVolume)
      && maximumPackVolume > minimumPackVolume;
    const packPosition = hasPackScale
      ? clamp(((packDimensions.volume - minimumPackVolume) / (maximumPackVolume - minimumPackVolume)) * 100, 0, 100)
      : 50;
    const packIconSize = packDimensions
      ? packIconSizeFor(packDimensions, minimumPackVolume, maximumPackVolume)
      : null;
    const packLabel = packDimensions
      ? `Packmaß: ${formatPackDimensions(packDimensions)}, ${formatPackVolume(packDimensions.volume)}`
      : 'Packmaß noch nicht hinterlegt';
    const root = document.createElement('section');

    root.className = 'product-performance-meters';
    root.dataset.productPerformanceMeters = '';
    root.innerHTML = `
      <h2 class="product-performance-meters__heading">Produktprofil</h2>
      <section class="product-performance-meter" aria-labelledby="ProductPerformanceWeight">
        <div class="product-performance-meter__heading">
          <span class="product-performance-meter__label" id="ProductPerformanceWeight">Gewicht</span>
        </div>
        <div class="product-performance-meter__weight-scale" data-performance-weight-scale>
          <div class="product-performance-meter__track" data-performance-weight-track>
            <span class="product-performance-meter__weight-fill"></span>
            <span class="product-performance-meter__marker" aria-hidden="true"></span>
          </div>
          <div class="product-performance-meter__weight-value-row">
            <span class="product-performance-meter__weight-value" data-performance-weight-value></span>
          </div>
        </div>
      </section>
      <section class="product-performance-meter" aria-labelledby="ProductPerformanceTemperature">
        <div class="product-performance-meter__heading">
          <span class="product-performance-meter__label" id="ProductPerformanceTemperature">Temperaturbereich</span>
        </div>
        <div class="product-performance-meter__temperature-visual">
          <div class="product-performance-meter__track" style="--performance-start:${temperatureStart}; --performance-end:${temperatureEnd};">
            ${temperatureRange ? '<span class="product-performance-meter__temperature-zone"></span>' : ''}
            ${!hasProductZero ? '<span class="product-performance-meter__zero" aria-hidden="true"></span>' : ''}
            ${temperaturePoints.map((point) => `<span class="product-performance-meter__temperature-point-marker" style="--performance-point:${point.position}; --temperature-colour:${temperatureColorFor(point.position)};" aria-hidden="true"></span>`).join('')}
          </div>
          ${temperaturePoints.length ? `<div class="product-performance-meter__temperature-details" style="--temperature-point-count:${temperaturePoints.length};" aria-hidden="true">
            ${temperaturePoints.map((point) => `<span class="product-performance-meter__temperature-detail" data-temperature-key="${point.key}" style="--performance-point:${point.position};"><small>${point.label}</small><strong>${formatTemperature(point.value)}</strong></span>`).join('')}
          </div>` : ''}
        </div>
      </section>
      <section class="product-performance-meter" aria-labelledby="ProductPerformancePackSize">
        <div class="product-performance-meter__heading">
          <span class="product-performance-meter__label" id="ProductPerformancePackSize">Packmaß</span>
        </div>
        ${packDimensions ? `<div class="product-performance-meter__pack-visual" role="img" aria-label="${packLabel}">
          <div class="product-performance-meter__track"></div>
          <span class="product-performance-meter__pack-marker" style="--performance-value:${packPosition}; --pack-icon-width:${packIconSize.width}px; --pack-icon-height:${packIconSize.height}px;" aria-hidden="true">
            <span class="product-performance-meter__pack-dimensions">${formatPackDimensions(packDimensions)}</span>
            <span class="product-performance-meter__pack-bag"></span>
            <span class="product-performance-meter__pack-volume">${formatPackVolume(packDimensions.volume)}</span>
          </span>
        </div>` : '<p class="product-performance-meter__pack-empty">Noch nicht hinterlegt</p>'}
      </section>
    `;

    const updateWeight = () => {
      const weight = parseWeight(data.weight);
      const scaleValues = (Array.isArray(data.weightScaleValues) ? data.weightScaleValues : [])
        .map(parseWeight)
        .filter((value) => Number.isFinite(value) && value > 0);
      const minimum = scaleValues.length ? Math.min(...scaleValues) : null;
      const maximum = scaleValues.length ? Math.max(...scaleValues) : null;
      const hasScale = Number.isFinite(weight) && minimum > 0 && maximum > minimum;
      const progress = hasScale ? clamp(((weight - minimum) / (maximum - minimum)) * 100, 0, 100) : 0;
      const track = root.querySelector('[data-performance-weight-track]');

      track.style.setProperty('--performance-value', progress);
      root.querySelector('[data-performance-weight-scale]').style.setProperty('--performance-value', progress);
      root.querySelector('[data-performance-weight-value]').textContent = formatWeight(weight);

    };

    host.replaceChildren(root);
    host.dataset.performanceMetersMounted = 'true';

    const layoutTemperatureLabels = () => {
      if (window.innerWidth <= 749) return;

      const detailsContainer = root.querySelector('.product-performance-meter__temperature-details');
      const details = Array.from(root.querySelectorAll('.product-performance-meter__temperature-detail'));
      if (!detailsContainer || !details.length) return;

      const availableWidth = detailsContainer.getBoundingClientRect().width;
      if (!availableWidth) return;

      // Reset first so labels are measured at their natural width.
      details.forEach((detail) => detail.style.removeProperty('--temperature-label-position'));

      const labelGap = 6;
      const labels = details
        .map((detail) => {
          const width = detail.getBoundingClientRect().width;
          const point = (Number(detail.style.getPropertyValue('--performance-point')) / 100) * availableWidth;
          const halfWidth = width / 2;

          return {
            detail,
            width,
            halfWidth,
            minimum: halfWidth,
            maximum: availableWidth - halfWidth,
            position: clamp(point, halfWidth, availableWidth - halfWidth),
          };
        })
        .sort((first, second) => first.position - second.position);

      // First give every label enough room from the one before it.
      for (let index = 1; index < labels.length; index += 1) {
        const previous = labels[index - 1];
        const current = labels[index];
        const minimumPosition = previous.position + previous.halfWidth + current.halfWidth + labelGap;
        current.position = Math.max(current.position, minimumPosition);
      }

      // Then pull the group back from the right, preserving the required gap.
      const last = labels[labels.length - 1];
      if (last.position > last.maximum) {
        last.position = last.maximum;

        for (let index = labels.length - 2; index >= 0; index -= 1) {
          const current = labels[index];
          const next = labels[index + 1];
          const maximumPosition = next.position - current.halfWidth - next.halfWidth - labelGap;
          current.position = Math.min(current.position, maximumPosition);
        }
      }

      labels.forEach(({ detail, position }) => {
        detail.style.setProperty('--temperature-label-position', `${Math.round(position * 10) / 10}px`);
      });
    };

    const scheduleTemperatureLabelLayout = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(layoutTemperatureLabels);
      });
    };

    scheduleTemperatureLabelLayout();
    window.addEventListener('load', scheduleTemperatureLabelLayout, { once: true });
    window.addEventListener('resize', scheduleTemperatureLabelLayout);

    if ('ResizeObserver' in window) {
      const temperatureLabelResizeObserver = new ResizeObserver(scheduleTemperatureLabelLayout);
      temperatureLabelResizeObserver.observe(root);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleTemperatureLabelLayout);
    }

    updateWeight();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountMeters, { once: true });
  } else {
    mountMeters();
  }

  document.addEventListener('shopify:section:load', mountMeters);
})();

